import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import UpdateIcon from '@mui/icons-material/Update';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import ProjectSCurve from './scurve/ProjectSCurve';
import ProjectFieldProgress from './progress/ProjectFieldProgress';
import ImportDialog from '../../components/import/ImportDialog';
import DownloadProgressDialog from '../../components/DownloadProgressDialog';
import { t } from '../../utils/textUtils';
import { useNotifier } from '../../hooks/useNotifier';

import { useProjectDetail } from './projectDetail/hooks/useProjectDetail';
import { useProjectPermissions } from './projectDetail/hooks/useProjectPermissions';

import ProjectHeader from './projectDetail/ProjectHeader';
import ProjectStatCards from './projectDetail/ProjectStatCards';
import TabPanel from './projectDetail/shared/TabPanel';

import GeneralInfoTab from './projectDetail/tabs/GeneralInfoTab';
import ScheduleTab from './projectDetail/tabs/ScheduleTab';
import WorkPlanTab from './projectDetail/tabs/WorkPlanTab';
import ProjectValueTab from './projectDetail/tabs/ProjectValueTab';
import TeamTab from './projectDetail/tabs/TeamTab';
import DocumentsTab from './projectDetail/tabs/DocumentsTab';
import ImportTab from './projectDetail/tabs/ImportTab';

import StatusChangeDialog from './projectDetail/dialogs/StatusChangeDialog';
import ManageEngineersDialog from './projectDetail/dialogs/ManageEngineersDialog';
import DownloadSubconDialog from './projectDetail/dialogs/DownloadSubconDialog';

import { PROJECT_TAB_INDEX } from './projectDetail/utils/constants';

const TABS_SX = {
  px: { xs: 1, sm: 2 },
  pt: 1,
  borderBottom: '1px solid',
  borderColor: 'rgba(15, 23, 42, 0.08)',
  minHeight: 48,
  bgcolor: '#F8FAFC',
  '& .MuiTabs-flexContainer': { gap: 0.5 },
  '& .MuiTab-root': {
    fontSize: '0.85rem',
    color: '#64748B',
    fontWeight: 600,
    textTransform: 'none',
    minHeight: 44,
    px: 2,
    borderRadius: '8px 8px 0 0',
    transition: 'color 160ms ease, background-color 160ms ease',
    '&:hover': {
      color: '#1e40af',
      backgroundColor: 'rgba(59, 130, 246, 0.06)'
    }
  },
  '& .Mui-selected': {
    color: '#1e40af !important',
    fontWeight: 700,
    backgroundColor: '#FFFFFF'
  },
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    background: 'linear-gradient(90deg, #3B82F6, #1e40af)'
  }
};

const CONTAINER_SX = {
  backgroundColor: '#FFFFFF',
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'rgba(15, 23, 42, 0.1)',
  mb: 3,
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)'
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { notifySuccess } = useNotifier();

  const { project, loading, error, refetch, updateProjectStatus } =
    useProjectDetail(projectId);
  const permissions = useProjectPermissions();

  const [tabValue, setTabValue] = useState(0);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [engineerDialogOpen, setEngineerDialogOpen] = useState(false);
  const [subconDialogOpen, setSubconDialogOpen] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#3B82F6' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/projects')}
          sx={{
            color: '#5D4037',
            borderColor: '#5D4037',
            '&:hover': { backgroundColor: '#F9F5EB', borderColor: '#8D6E63' }
          }}
        >
          {t('KEMBALI KE PROYEK')}
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('PROYEK TIDAK DITEMUKAN')}</Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/projects')}
          sx={{
            mt: 2,
            color: '#5D4037',
            borderColor: '#5D4037',
            '&:hover': { backgroundColor: '#F9F5EB', borderColor: '#8D6E63' }
          }}
        >
          {t('KEMBALI KE PROYEK')}
        </Button>
      </Box>
    );
  }

  const handleStatusSubmit = async (newStatus) => {
    try {
      await updateProjectStatus(newStatus);
      setStatusDialogOpen(false);
    } catch (err) {
      console.error('Error updating project status:', err);
    }
  };

  return (
    <Box sx={{ width: '100%', px: 0, ml: 0 }}>
      <ProjectHeader
        project={project}
        permissions={permissions}
        onEdit={() => navigate(`/projects/${projectId}/edit`)}
        onViewGantt={() => navigate(`/projects/${projectId}/gantt-new`)}
        onDownloadAttachments={() => setDownloadDialogOpen(true)}
        onDownloadSubcon={() => setSubconDialogOpen(true)}
        onChangeStatus={() => setStatusDialogOpen(true)}
      />

      <ProjectStatCards project={project} />

      <Box sx={CONTAINER_SX}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={TABS_SX}
        >
          <Tab label={t('INFORMASI UMUM')} />
          <Tab label={t('JADWAL & TIMELINE')} />
          <Tab label={t('RENCANA KERJA & VOLUME')} />
          <Tab label={t('NILAI PROYEK')} />
          <Tab label={t('TIM')} />
          <Tab label={t('DOKUMEN')} />
          <Tab label={t('KURVA-S')} icon={<ShowChartIcon />} iconPosition="start" />
          <Tab label={t('UPDATE LAPANGAN')} icon={<UpdateIcon />} iconPosition="start" />
          <Tab label={t('UPLOAD PEKERJAAN')} icon={<CloudUploadIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.GENERAL}>
          <GeneralInfoTab project={project} />
        </TabPanel>
        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.SCHEDULE}>
          <ScheduleTab project={project} />
        </TabPanel>
        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.WORK_PLAN}>
          <WorkPlanTab project={project} />
        </TabPanel>
        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.VALUE}>
          <ProjectValueTab project={project} />
        </TabPanel>
        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.TEAM}>
          <TeamTab
            project={project}
            canEdit={permissions.canEdit}
            onManageEngineers={() => setEngineerDialogOpen(true)}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.DOCUMENTS}>
          <DocumentsTab project={project} />
        </TabPanel>
        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.S_CURVE}>
          <ProjectSCurve projectId={projectId} projectData={project} />
        </TabPanel>
        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.FIELD_PROGRESS}>
          <ProjectFieldProgress projectId={projectId} projectData={project} />
        </TabPanel>
        <TabPanel value={tabValue} index={PROJECT_TAB_INDEX.IMPORT}>
          <ImportTab
            project={project}
            projectId={projectId}
            onOpenImport={() => setImportDialogOpen(true)}
            onImportSuccess={() => {
              notifySuccess(t('IMPORT BERHASIL'));
              refetch();
            }}
          />
        </TabPanel>
      </Box>

      <StatusChangeDialog
        open={statusDialogOpen}
        currentStatus={project.status}
        onClose={() => setStatusDialogOpen(false)}
        onSubmit={handleStatusSubmit}
      />

      <ManageEngineersDialog
        open={engineerDialogOpen}
        project={project}
        projectId={projectId}
        onClose={() => setEngineerDialogOpen(false)}
        onSuccess={refetch}
      />

      <DownloadSubconDialog
        open={subconDialogOpen}
        projectId={projectId}
        onClose={() => setSubconDialogOpen(false)}
      />

      <DownloadProgressDialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        project={project}
      />

      <ImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        projectId={projectId}
        onImportSuccess={() => {
          notifySuccess(t('IMPORT BERHASIL'));
          setImportDialogOpen(false);
          refetch();
        }}
        initialStep={1}
      />
    </Box>
  );
};

export default ProjectDetail;
